import { inMinutes } from "@@/shared/date-time/time-spans";
import { toObjectMap } from "@@/utilities/dictionary";
import type { IconDefinition } from "@fortawesome/fontawesome-svg-core";
import {
    faFileInvoiceDollar,
    faFilePdf,
} from "@fortawesome/pro-regular-svg-icons";
import { DAYS } from "../date-time/time-constants";
import type {
    RequiredTranslatable,
    Translatable} from "../translations/translation";
import {
    translation,
} from "../translations/translation";
import type { DefaultJobOptions, MetricsOptions } from "./bullmq-interfaces";

type AlfredQueue = {
    name: string;
    title: Translatable;
    icon: IconDefinition;
    description: Translatable;
    defaultJobOptions: DefaultJobOptions;
    defaultWorkerMetricOptions: MetricsOptions;
};

const defaultJobOptions: DefaultJobOptions = {
    removeOnComplete: {
        age: 1 * DAYS,
    },
    removeOnFail: {
        age: 1 * DAYS,
    },
};
const defaultWorkerMetricOptions: MetricsOptions = {
    maxDataPoints: inMinutes({ days: 14 }),
};

const alfredQueueKeys = [
    "settlementNoteCreation",
    "settlementNotePdfCreation",
    "iCalImportCreation",
    "channexSyncQueue",
    "channexSyncBookingQueue",
    "channexProcessBookingQueue",
] as const;
const alfredQueueNames = [
    "settlement-note-creation",
    "settlement-note-pdf-creation",
    "ical-import-creation",
    "channex-sync-queue",
    "channex-sync-booking-queue",
    "channex-process-booking-queue",
] as const;
const alfredQueueName = toObjectMap([...alfredQueueNames]);

type AlfredQueueKey = (typeof alfredQueueKeys)[number];
type AlfredQueueName = (typeof alfredQueueNames)[number];
type AlfredQueueMeta<Key extends AlfredQueueKey = AlfredQueueKey> = {
    key: Key;
    name: AlfredQueueName;
    title: RequiredTranslatable;
    icon: IconDefinition;
    description: RequiredTranslatable;
    defaultJobOptions: DefaultJobOptions;
    defaultWorkerMetricOptions: MetricsOptions;
};
const alfredQueues: {
    [Key in AlfredQueueKey]: AlfredQueueMeta<Key>;
} = {
    settlementNoteCreation: {
        key: "settlementNoteCreation",
        name: "settlement-note-creation",
        title: translation({
            sv: "Avräkningsnotor",
            en: "Settlement notes",
        }),
        icon: faFileInvoiceDollar,
        description: translation({
            sv: "Kön bearbetar förfrågningar om att skapa avräkningsnotor",
            en: "Queue processes requests to create settlement notes",
        }),
        defaultJobOptions,
        defaultWorkerMetricOptions,
    },
    settlementNotePdfCreation: {
        key: "settlementNotePdfCreation",
        name: "settlement-note-pdf-creation",
        title: translation({
            sv: "Avräkningsnotor - PDF",
            en: "Settlement notes - PDF",
        }),
        icon: faFilePdf,
        description: translation({
            sv: "Kön bearbetar förfrågningar för att skapa pdf:er för avräkningsnotor",
            en: "Queue processes requests to create pdfs for settlement notes",
        }),
        defaultJobOptions,
        defaultWorkerMetricOptions,
    },
    iCalImportCreation: {
        key: "iCalImportCreation",
        name: "ical-import-creation",
        title: translation({
            sv: "iCal-import",
            en: "iCal-import",
        }),
        icon: faFilePdf,
        description: translation({
            sv: "Kön bearbetar förfrågningar om att utföra iCal-import",
            en: "Queue processes requests to create iCal-import",
        }),
        defaultJobOptions,
        defaultWorkerMetricOptions,
    },
    channexSyncQueue: {
        key: "channexSyncQueue",
        name: "channex-sync-queue",
        title: translation({
            sv: "Synkronisera ändring till Channex",
            en: "Sync with Channex",
        }),
        icon: faFilePdf,
        description: translation({
            sv: "Kön bearbetar förfrågningar om att synkronisera med nytt data till Channex",
            en: "Queue processes requests to sync with Channex",
        }),
        defaultJobOptions,
        defaultWorkerMetricOptions,
    },
    channexSyncBookingQueue: {
        key: "channexSyncBookingQueue",
        name: "channex-sync-booking-queue",
        title: translation({
            sv: "Synkronisera bokningsfeed från Channex (10min cron)",
            en: "Sync booking with Channex",
        }),
        icon: faFilePdf,
        description: translation({
            sv: "Jobb för att synkronisera nya bokningar med Channex - kollar var 10:e minut om det finns något att synkronisera",
            en: "Job to sync booking with Channex",
        }),
        defaultJobOptions,
        defaultWorkerMetricOptions,
    },
    channexProcessBookingQueue: {
        key: "channexProcessBookingQueue",
        name: "channex-process-booking-queue",
        title: translation({
            sv: "Bearbeta bokning från Channex",
            en: "Process booking with Channex",
        }),
        icon: faFilePdf,
        description: translation({
            sv: "Jobb för att bearbeta bokning med Channex",
            en: "Job to process booking with Channex",
        }),
        defaultJobOptions,
        defaultWorkerMetricOptions,
    },
};

export { alfredQueueKeys, alfredQueueName, alfredQueueNames, alfredQueues };

export type { AlfredQueue, AlfredQueueKey, AlfredQueueMeta, AlfredQueueName };
